import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Save, X, Check, ExternalLink } from "lucide-react";
import { Logo } from "@/components/Logo";
import {
  authMe,
  authLogout,
  adminListAllHotels,
  adminCreateHotel,
  adminUpdateHotel,
  adminDeleteHotel,
  adminApproveHotel,
  adminRejectHotel,
  adminListWaitlist,
  fileUrl,
} from "@/lib/api";

const MIN_GOAL = 350000;
const emptyHotel = {
  name: "",
  location: "",
  description: "",
  image_url: "",
  gallery: [],
  goal_amount: MIN_GOAL,
  raised_amount: 0,
  days_left: 30,
  quota: 49,
  stay_days: 4,
};

const STATUS_BADGE = {
  approved: "border-emerald-300 text-emerald-700",
  pending: "border-amber-300 text-amber-700",
  rejected: "border-red-300 text-red-700",
  draft: "border-neutral-300 text-neutral-500",
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    authMe()
      .then((u) => {
        setUser(u);
        setChecking(false);
        refresh();
      })
      .catch(() => navigate("/admin-login", { replace: true }));
  }, [navigate]);

  const refresh = async () => {
    try {
      const [h, w] = await Promise.all([adminListAllHotels(), adminListWaitlist()]);
      setHotels(h);
      setWaitlist(w);
    } catch (e) {
      // ignored
    }
  };

  const logout = async () => {
    await authLogout();
    navigate("/admin-login", { replace: true });
  };

  const saveHotel = async (payload, hotel_id) => {
    if (payload.goal_amount < MIN_GOAL) {
      toast.error(`Il target minimo di raccolta è ${MIN_GOAL.toLocaleString("it-IT")} €.`);
      return;
    }
    try {
      if (hotel_id === "new") await adminCreateHotel(payload);
      else await adminUpdateHotel(hotel_id, payload);
      toast.success("Hotel salvato");
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo hotel?")) return;
    await adminDeleteHotel(id);
    toast.success("Hotel eliminato");
    refresh();
  };

  const approve = async (id) => {
    await adminApproveHotel(id);
    toast.success("Progetto approvato");
    refresh();
  };
  const reject = async (id) => {
    await adminRejectHotel(id);
    toast.success("Progetto rifiutato");
    refresh();
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm tracking-wider">
        Verifica sessione...
      </div>
    );
  }

  const pendingCount = hotels.filter((h) => h.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#F7F7F8]">
      <header className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex justify-between items-center">
          <div className="flex items-center gap-8">
            <Logo variant="dark" size="md" />
            <div className="sc-overline">Console</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-xs text-neutral-500">{user?.email}</div>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="sc-btn-ghost inline-flex items-center gap-2"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 space-y-20">
        {pendingCount > 0 && (
          <div className="border border-amber-200 bg-amber-50 p-6 md:p-8 flex items-center justify-between gap-4">
            <div>
              <div className="sc-overline mb-1" style={{ color: "#78350F" }}>
                Richieste in attesa
              </div>
              <div className="text-sm text-amber-900">
                {pendingCount} {pendingCount === 1 ? "progetto è" : "progetti sono"} in attesa di approvazione.
              </div>
            </div>
          </div>
        )}

        {/* Waitlist */}
        <section>
          <div className="flex justify-between items-end mb-8">
            <div>
              <div className="sc-overline mb-2">Waitlist</div>
              <h2 className="font-display font-light text-3xl" style={{ letterSpacing: "-0.02em" }}>
                {waitlist.length} iscritti
              </h2>
            </div>
          </div>
          <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead>
                <tr className="border-b border-[#E5E5E5]">
                  <th className="text-left py-4 px-6 sc-overline">#</th>
                  <th className="text-left py-4 px-6 sc-overline">Email</th>
                  <th className="text-left py-4 px-6 sc-overline">Code</th>
                  <th className="text-left py-4 px-6 sc-overline">Referrals</th>
                  <th className="text-left py-4 px-6 sc-overline">Invitato da</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.slice(0, 50).map((w) => (
                  <tr key={w.user_id} className="border-b border-[#F0F0F0] last:border-none">
                    <td className="py-4 px-6 text-[#0B192C] font-medium">#{w.position}</td>
                    <td className="py-4 px-6">{w.email}</td>
                    <td className="py-4 px-6 font-mono text-xs text-neutral-500">{w.referral_code}</td>
                    <td className="py-4 px-6">{w.referrals_count}</td>
                    <td className="py-4 px-6 text-neutral-500">{w.referred_by || "—"}</td>
                  </tr>
                ))}
                {waitlist.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-10 px-6 text-center text-neutral-400">
                      Nessun iscritto ancora.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Hotels */}
        <section>
          <div className="flex justify-between items-end mb-8">
            <div>
              <div className="sc-overline mb-2">Progetti Hotel</div>
              <h2 className="font-display font-light text-3xl" style={{ letterSpacing: "-0.02em" }}>
                Gestione progetti
              </h2>
            </div>
            <button
              data-testid="add-hotel-btn"
              onClick={() => setEditing("new")}
              className="sc-btn-primary inline-flex items-center gap-2"
            >
              <Plus size={14} /> Nuovo progetto
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {editing === "new" && (
              <HotelForm
                initial={emptyHotel}
                onSave={(p) => saveHotel(p, "new")}
                onCancel={() => setEditing(null)}
              />
            )}
            {hotels.map((h) =>
              editing === h.hotel_id ? (
                <HotelForm
                  key={h.hotel_id}
                  initial={h}
                  onSave={(p) => saveHotel(p, h.hotel_id)}
                  onCancel={() => setEditing(null)}
                />
              ) : (
                <div
                  key={h.hotel_id}
                  data-testid={`hotel-card-${h.hotel_id}`}
                  className="bg-white border border-[#E5E5E5] overflow-hidden"
                >
                  {h.image_url ? (
                    <img
                      src={fileUrl(h.image_url)}
                      alt={h.name}
                      className="w-full h-40 object-cover"
                    />
                  ) : (
                    <div className="w-full h-40 bg-neutral-100 flex items-center justify-center text-neutral-400 text-[11px] tracking-widest uppercase">
                      Nessuna foto
                    </div>
                  )}
                  <div className="p-6 space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-display text-lg font-normal">{h.name}</div>
                        <div className="text-xs text-neutral-500 mt-1">{h.location}</div>
                      </div>
                      <span
                        className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${STATUS_BADGE[h.status] || STATUS_BADGE.draft}`}
                      >
                        {h.status}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-600">
                      {(h.raised_amount || 0).toLocaleString("it-IT")} /{" "}
                      {(h.goal_amount || 0).toLocaleString("it-IT")} € · {h.days_left}gg
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {h.status !== "approved" && (
                        <button
                          onClick={() => approve(h.hotel_id)}
                          className="sc-btn-primary text-[10px] inline-flex items-center gap-1"
                          data-testid={`approve-hotel-${h.hotel_id}`}
                          style={{ padding: "8px 14px" }}
                        >
                          <Check size={12} /> Approva
                        </button>
                      )}
                      {h.status === "pending" && (
                        <button
                          onClick={() => reject(h.hotel_id)}
                          className="sc-btn-ghost text-[10px]"
                          data-testid={`reject-hotel-${h.hotel_id}`}
                        >
                          Rifiuta
                        </button>
                      )}
                      {h.status === "approved" && (
                        <a
                          href={`/hotel/${h.hotel_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="sc-btn-ghost text-[10px] inline-flex items-center gap-1"
                        >
                          <ExternalLink size={12} /> Anteprima
                        </a>
                      )}
                      <button
                        onClick={() => setEditing(h.hotel_id)}
                        className="sc-btn-ghost text-[10px]"
                        data-testid={`edit-hotel-${h.hotel_id}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => del(h.hotel_id)}
                        className="sc-btn-ghost text-[10px] inline-flex items-center gap-1"
                        data-testid={`delete-hotel-${h.hotel_id}`}
                      >
                        <Trash2 size={12} /> Elimina
                      </button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function HotelForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm({ ...form, [k]: v });
  };
  return (
    <div className="bg-white border border-[#0B192C] p-6 space-y-4">
      <div className="sc-overline">
        {initial.hotel_id ? "Modifica progetto" : "Nuovo progetto"}
      </div>
      {[
        ["name", "Nome hotel", "text"],
        ["location", "Località", "text"],
        ["image_url", "URL immagine", "text"],
      ].map(([k, label, type]) => (
        <div key={k}>
          <label className="sc-overline block mb-1">{label}</label>
          <input type={type} value={form[k]} onChange={set(k)} className="sc-input-light" />
        </div>
      ))}
      <div>
        <label className="sc-overline block mb-1">Descrizione</label>
        <textarea rows={4} value={form.description} onChange={set("description")} className="sc-input-light" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[
          ["goal_amount", `Target € (min ${MIN_GOAL.toLocaleString("it-IT")})`],
          ["raised_amount", "Raccolti €"],
          ["days_left", "Giorni rimasti"],
          ["quota", "Quota €"],
          ["stay_days", "Giorni voucher"],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="sc-overline block mb-1">{label}</label>
            <input type="number" value={form[k]} onChange={set(k)} className="sc-input-light" />
          </div>
        ))}
      </div>
      <div className="flex gap-3 pt-4">
        <button onClick={() => onSave(form)} className="sc-btn-primary inline-flex items-center gap-2">
          <Save size={14} /> Salva
        </button>
        <button onClick={onCancel} className="sc-btn-ghost inline-flex items-center gap-2">
          <X size={14} /> Annulla
        </button>
      </div>
    </div>
  );
}
